import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Bookmark, 
  BookmarkCheck, 
  Trash2, 
  Play, 
  Search, 
  Filter, 
  Calendar, 
  Tag,
  FileText,
  BookOpen,
  ExternalLink,
  Plus,
  Edit3
} from 'lucide-react';
import { 
  getUserSavedQuestions, 
  removeSavedQuestion,
  getUserBookmarks,
  removeBookmark,
  saveBookmark,
  listenToUserBookmarks,
  listenToUserSavedQuestions,
  getUserQuestionBookmarks,
  removeQuestionBookmark,
  listenToUserQuestionBookmarks
} from '../../firebase/collections';
import { useRealtime } from '../../contexts/RealtimeContext';
import { QuestionComponent } from '../../components/QuestionTypes';

const Bookmarks = () => {
  const navigate = useNavigate();
  const { currentUser } = useRealtime();
  const [savedQuestions, setSavedQuestions] = useState([]);
  const [questionBookmarks, setQuestionBookmarks] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [showPracticeModal, setShowPracticeModal] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [activeTab, setActiveTab] = useState('questions'); // 'questions', 'resources', 'notes'

  // Get unique categories and types from bookmarks
  const categories = ['all', ...new Set(bookmarks.map(b => b.category).filter(Boolean))];
  const types = ['all', ...new Set(bookmarks.map(b => b.type).filter(Boolean))];
  
  // Get unique subjects and difficulties from question bookmarks
  const subjects = ['all', ...new Set(questionBookmarks.map(q => q.subject).filter(Boolean))];
  const difficulties = ['all', ...new Set(questionBookmarks.map(q => q.difficulty).filter(Boolean))];

  useEffect(() => {
    if (currentUser?.uid) {
      loadAllBookmarks();
    }
  }, [currentUser?.uid]);

  // Real-time listeners for bookmarks and saved questions
  useEffect(() => {
    if (currentUser?.uid) {
      const unsubscribeBookmarks = listenToUserBookmarks(currentUser.uid, (result) => {
        if (result.success) {
          setBookmarks(result.data);
        }
      });

      const unsubscribeQuestionBookmarks = listenToUserQuestionBookmarks(currentUser.uid, (result) => {
        if (result.success) {
          setQuestionBookmarks(result.data);
        }
      });

      const unsubscribeSavedQuestions = listenToUserSavedQuestions(currentUser.uid, (result) => {
        if (result.success) {
          setSavedQuestions(result.data);
        }
      });

      return () => {
        unsubscribeBookmarks();
        unsubscribeQuestionBookmarks();
        unsubscribeSavedQuestions();
      };
    }
  }, [currentUser?.uid]);

  const loadAllBookmarks = async () => {
    try {
      setLoading(true);
      
      // Load question bookmarks (new system)
      const questionBookmarksResult = await getUserQuestionBookmarks(currentUser.uid);
      if (questionBookmarksResult.success) {
        setQuestionBookmarks(questionBookmarksResult.data);
      }
      
      // Load old saved questions (legacy)
      const questionsResult = await getUserSavedQuestions(currentUser.uid);
      if (questionsResult.success) {
        setSavedQuestions(questionsResult.data);
      }
      
      // Load resources and notes bookmarks
      const bookmarksResult = await getUserBookmarks(currentUser.uid);
      if (bookmarksResult.success) {
        setBookmarks(bookmarksResult.data);
      }
    } catch (error) {
      // Error loading bookmarks
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveQuestion = async (questionId) => {
    try {
      const result = await removeQuestionBookmark(questionId);
      if (result.success) {
        setQuestionBookmarks(prev => prev.filter(q => q.id !== questionId));
      } else {
      }
    } catch (error) {
      // Error removing question
    }
  };

  const handleRemoveBookmark = async (bookmarkId) => {
    try {
      const result = await removeBookmark(bookmarkId);
      if (result.success) {
        setBookmarks(prev => prev.filter(b => b.id !== bookmarkId));
      } else {
      }
    } catch (error) {
      // Error removing bookmark
    }
  };

  const handleStartPractice = () => {
    if (selectedQuestions.length === 0) {
      return;
    }

    // Create practice test data
    const practiceData = {
      id: `practice-${Date.now()}`,
      name: 'Practice with Saved Questions',
      questions: selectedQuestions.map(q => ({
        id: q.questionId,
        question: q.question,
        type: q.type,
        options: q.options,
        correctAnswer: q.correctAnswer,
        explanation: q.explanation,
        topic: q.topic,
        difficulty: q.difficulty,
        markingScheme: q.markingScheme
      })),
      duration: selectedQuestions.length * 2 * 60, // 2 minutes per question
      topic: 'Mixed Topics'
    };

    // Store in localStorage for the test interface to pick up
    localStorage.setItem(`practice-test-${practiceData.id}`, JSON.stringify(practiceData));
    
    // Navigate to test interface
    navigate(`/dashboard/test/${practiceData.id}`);
  };

  const toggleQuestionSelection = (questionId) => {
    setSelectedQuestions(prev => 
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const selectAllQuestions = () => {
    const filteredQuestions = getFilteredQuestions();
    setSelectedQuestions(filteredQuestions.map(q => q.id));
  };

  const clearSelection = () => {
    setSelectedQuestions([]);
  };

  const getFilteredQuestions = () => {
    let filtered = questionBookmarks;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(q => 
        q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.topic?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.subject?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const getFilteredBookmarks = () => {
    let filtered = bookmarks;

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(b => b.type === selectedType);
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(b => b.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(b => 
        b.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const getTotalBookmarks = () => {
    return questionBookmarks.length + bookmarks.length;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading bookmarks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Bookmarks
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Your saved questions, resources, and notes
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">{getTotalBookmarks()}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Bookmarks</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md mb-8">
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab('questions')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'questions'
                  ? 'text-blue-600 border-b-2 border-blue-600 dark:text-blue-400 dark:border-blue-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <BookOpen className="w-4 h-4 mr-2 inline" />
              Questions ({questionBookmarks.length})
            </button>
            <button
              onClick={() => setActiveTab('resources')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'resources'
                  ? 'text-green-600 border-b-2 border-green-600 dark:text-green-400 dark:border-green-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <FileText className="w-4 h-4 mr-2 inline" />
              Resources ({bookmarks.filter(b => b.type === 'resource').length})
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'notes'
                  ? 'text-purple-600 border-b-2 border-purple-600 dark:text-purple-400 dark:border-purple-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Bookmark className="w-4 h-4 mr-2 inline" />
              Notes ({bookmarks.filter(b => b.type === 'note').length})
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              {activeTab === 'questions' && (
                <>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category === 'all' ? 'All Categories' : category}
                      </option>
                    ))}
                  </select>
                </>
              )}
              
              {activeTab !== 'questions' && (
                <>
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {types.map(type => (
                      <option key={type} value={type}>
                        {type === 'all' ? 'All Types' : type}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>

            {activeTab === 'questions' && (
              <div className="flex space-x-2">
                <button
                  onClick={selectAllQuestions}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Select All
                </button>
                <button
                  onClick={clearSelection}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                  Clear
                </button>
                <button
                  onClick={() => setShowPracticeModal(true)}
                  disabled={selectedQuestions.length === 0}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Play className="h-4 w-4 mr-2 inline" />
                  Practice Selected ({selectedQuestions.length})
                </button>
              </div>
            )}
          </div>

          <div className="mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={`Search ${activeTab}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'questions' && (
          <div>
            {getFilteredQuestions().length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {questionBookmarks.length === 0 ? 'No bookmarked questions yet' : 'No questions match your filters'}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {questionBookmarks.length === 0 
                    ? 'Start taking tests and bookmark questions you want to practice later'
                    : 'Try adjusting your search or filter criteria'
                  }
                </p>
                {questionBookmarks.length === 0 && (
                  <button
                    onClick={() => navigate('/dashboard/mock-tests')}
                    className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Take a Test
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {getFilteredQuestions().map((savedQ, index) => (
                  <motion.div
                    key={savedQ.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={selectedQuestions.includes(savedQ.id)}
                            onChange={() => toggleQuestionSelection(savedQ.id)}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                          <div className="flex items-center space-x-2">
                            <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm">
                              {savedQ.subject || savedQ.topic || 'General'}
                            </span>
                            <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-full text-sm">
                              {savedQ.difficulty || 'Medium'}
                            </span>
                            {savedQ.testName && (
                              <span className="px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-sm">
                                {savedQ.testName}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleRemoveQuestion(savedQ.id)}
                            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            title="Remove from saved questions"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      <div className="mb-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                          {savedQ.question}
                        </h3>
                      </div>

                      {/* Question Options */}
                      <div className="mb-4">
                        <QuestionComponent
                          question={{
                            id: savedQ.questionId,
                            question: savedQ.question,
                            type: savedQ.type,
                            options: savedQ.options,
                            correctAnswer: savedQ.correctAnswer,
                            explanation: savedQ.explanation,
                            topic: savedQ.topic,
                            difficulty: savedQ.difficulty,
                            markingScheme: savedQ.markingScheme
                          }}
                          selectedAnswer={null}
                          onAnswerSelect={() => {}}
                          isReview={true}
                          correctAnswer={savedQ.correctAnswer}
                        />
                      </div>

                      <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center space-x-4">
                          {savedQ.testName && <span>From: {savedQ.testName}</span>}
                          <span>Bookmarked: {new Date(savedQ.createdAt?.toDate?.() || savedQ.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {(activeTab === 'resources' || activeTab === 'notes') && (
          <div>
            {getFilteredBookmarks().length === 0 ? (
              <div className="text-center py-12">
                {activeTab === 'resources' ? (
                  <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                ) : (
                  <Bookmark className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                )}
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {bookmarks.filter(b => b.type === activeTab.slice(0, -1)).length === 0 
                    ? `No saved ${activeTab} yet` 
                    : 'No items match your filters'
                  }
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {bookmarks.filter(b => b.type === activeTab.slice(0, -1)).length === 0 
                    ? `Start bookmarking ${activeTab} you want to save for later`
                    : 'Try adjusting your search or filter criteria'
                  }
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {getFilteredBookmarks().map((bookmark, index) => (
                  <motion.div
                    key={bookmark.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          {bookmark.type === 'resource' ? (
                            <FileText className="w-5 h-5 text-green-600" />
                          ) : (
                            <Bookmark className="w-5 h-5 text-purple-600" />
                          )}
                          <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-full text-xs">
                            {bookmark.type}
                          </span>
                        </div>
                        <button
                          onClick={() => handleRemoveBookmark(bookmark.id)}
                          className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                          title="Remove bookmark"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>

                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        {bookmark.title}
                      </h3>
                      
                      {bookmark.description && (
                        <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
                          {bookmark.description}
                        </p>
                      )}

                      {bookmark.url && (
                        <a
                          href={bookmark.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Open Link
                        </a>
                      )}

                      <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                        Saved: {new Date(bookmark.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Practice Modal */}
      {showPracticeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Start Practice Session
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You're about to start a practice session with {selectedQuestions.length} selected questions.
              This will create a timed test with these questions.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowPracticeModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowPracticeModal(false);
                  handleStartPractice();
                }}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Start Practice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookmarks;
